import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, Clock, ListChecks } from 'lucide-react';
import HussarTopNav from '../components/HussarTopNav';

/**
 * WiedzaAiWAktachImigracyjnych
 *
 * First SEO long-form article — anchors the empty PL search niche around
 * "AI w aktach imigracyjnych" / "jak uporządkować akta imigracyjne" with
 * a calm, educational, people-first 1500+ word piece.
 *
 * Strict scope:
 *  - Static page. No state. No forms. No uploads. No API. No auth.
 *  - Backend untouched.
 *  - Tone: educational · calm · human · institution-friendly · legally cautious.
 *  - No legal advice. No status-verification claim. No certification claim.
 */
const META_TITLE = 'AI w aktach imigracyjnych | HussarAi';
const META_DESCRIPTION =
  'Jak uporządkować akta imigracyjne z pomocą AI bez oddawania decyzji ' +
  'maszynie. Dokumenty, terminy, ślad audytowy i weryfikacja przez człowieka.';
const CANONICAL_URL = 'https://hussarai.org/wiedza/ai-w-aktach-imigracyjnych';

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('meta') ? 'meta' : 'link');
    if (selector.includes('property=')) {
      const prop = selector.match(/property="([^"]+)"/)?.[1];
      if (prop) el.setAttribute('property', prop);
    } else if (selector.includes('name=')) {
      const name = selector.match(/name="([^"]+)"/)?.[1];
      if (name) el.setAttribute('name', name);
    } else if (selector.includes('rel=')) {
      const rel = selector.match(/rel="([^"]+)"/)?.[1];
      if (rel) el.setAttribute('rel', rel);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const WiedzaAiWAktachImigracyjnych = () => {
  useEffect(() => {
    document.title = META_TITLE;
    setMeta('meta[name="description"]', 'content', META_DESCRIPTION);
    setMeta('meta[property="og:title"]', 'content', META_TITLE);
    setMeta('meta[property="og:description"]', 'content', META_DESCRIPTION);
    setMeta('meta[property="og:type"]', 'content', 'article');
    setMeta('meta[property="og:url"]', 'content', CANONICAL_URL);
    setMeta('link[rel="canonical"]', 'href', CANONICAL_URL);
  }, []);

  return (
    <main
      data-testid="wiedza-ai-akta-page"
      style={{
        minHeight: '100vh',
        background: 'var(--hussar-bg-page, #0B1220)',
        color: 'var(--hussar-text-primary, #E5E7EB)',
        padding: '56px 20px 96px',
      }}
    >
      <HussarTopNav />
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <Link
          to="/"
          data-testid="wiedza-back-link"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: 'rgba(125,211,252,0.85)',
            textDecoration: 'none', marginBottom: 28,
          }}
        >
          <ArrowLeft className="w-4 h-4" /> Powrót do HussarAi
        </Link>

        <p
          data-testid="wiedza-eyebrow"
          style={{
            fontSize: 11, letterSpacing: 1.6, fontWeight: 700,
            color: '#7DD3FC', textTransform: 'uppercase', marginBottom: 14,
          }}
        >
          WIEDZA · AI W AKTACH IMIGRACYJNYCH · POD KONTROLĄ CZŁOWIEKA
        </p>

        <h1
          data-testid="wiedza-h1"
          style={{
            fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800,
            lineHeight: 1.2, marginBottom: 18, color: '#F1F5F9',
          }}
        >
          AI w aktach imigracyjnych: jak uporządkować dokumenty bez oddawania
          decyzji maszynie
        </h1>

        <p
          data-testid="wiedza-lede"
          style={{
            fontSize: 18, lineHeight: 1.55, fontWeight: 600,
            color: '#F1F5F9', marginBottom: 8,
          }}
        >
          Twoja historia nie powinna zniknąć w papierach.
        </p>

        <p
          data-testid="wiedza-intro"
          style={{
            fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)',
            marginBottom: 28,
          }}
        >
          Życie imigracyjne ludzi jest najczęściej rozproszone: paszport leży
          w szufladzie, decyzje urzędowe są w mailu sprzed dwóch lat, umowa o
          pracę u pracodawcy, zaświadczenie ze szkoły w szafce dziecka, a
          terminy odnowienia karty pobytu w notesie babci. Akta imigracyjne
          rzadko żyją w jednym miejscu. Powstaje w ten sposób cichy,
          długotrwały koszt: rozmowy z prawnikiem zaczynają się od&nbsp;szukania,
          a nie od&nbsp;sprawy. W&nbsp;tym artykule wyjaśniamy, jak AI może
          pomóc uporządkować akta imigracyjne — i&nbsp;dlaczego decyzję o
          statusie prawnym musi nadal podejmować człowiek.
        </p>

        <h2 data-testid="wiedza-h2-records" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Czym są akta imigracyjne?
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Akta imigracyjne to suma dokumentów i informacji, które razem opisują
          obecność osoby w danym kraju lub kilku krajach. W praktyce mogą obejmować:
        </p>
        <ul style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}>
          <li>dokumenty tożsamości (paszport, dowód, akty urodzenia, akty małżeństwa);</li>
          <li>historię podróży (stemple graniczne, bilety, wcześniejsze pobyty);</li>
          <li>dokumenty pobytowe (wiza, karta pobytu, decyzje o zezwoleniu na pobyt czasowy, stały, status uchodźcy);</li>
          <li>dokumenty pracy (zezwolenie na pracę, umowa o pracę, oświadczenia pracodawcy, dokumenty rejestracji firmy);</li>
          <li>dokumenty szkolne (zaświadczenia, dyplomy, listy z uczelni, dokumenty dziecka);</li>
          <li>kontekst rodzinny (małżonek, dzieci, osoby zależne, sponsorzy);</li>
          <li>pisma urzędowe (wezwania, decyzje, postanowienia, odwołania);</li>
          <li>terminy, wnioski, daty wizyt, terminy odpowiedzi;</li>
          <li>notatki z weryfikacji przez prawnika, NGO, doradcę.</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          To jest historia człowieka w dokumentach. Im bardziej rozproszona,
          tym trudniej ją obronić, wyjaśnić i przygotować do weryfikacji przez
          człowieka — czy to prawnika, NGO, pracodawcę, czy urząd.
        </p>

        <h2 data-testid="wiedza-h2-ai-can-help" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Gdzie AI może pomóc
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Sztuczna inteligencja sprawdza się dobrze w&nbsp;pracy z dużą liczbą
          dokumentów, dat i pól tekstowych. W obszarze akt imigracyjnych AI
          może odpowiedzialnie pomóc w:
        </p>
        <ul style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}>
          <li>streszczeniu sprawy (case summary) w neutralnym, ustrukturyzowanym formacie;</li>
          <li>wskazaniu brakujących informacji (missing-document detection);</li>
          <li>budowie checklisty dokumentów do skompletowania;</li>
          <li>spójności osi czasu (timeline consistency): wjazdy, wyjazdy, daty wniosków, terminy odnowienia;</li>
          <li>widoczności terminów (deadline visibility) — co kończy się za 30 / 60 / 90 dni;</li>
          <li>notatkach do weryfikacji przez człowieka (reviewer notes);</li>
          <li>pytaniach do tłumaczenia (co wymaga tłumaczenia przysięgłego, co poświadczonego);</li>
          <li>śladzie audytowym — kto i kiedy dodał dokument, zmienił notatkę, zatwierdził element.</li>
        </ul>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}>
          We wszystkich tych obszarach AI pełni rolę przygotowującą. Wynik
          trafia do człowieka — osoby zainteresowanej, prawnika, NGO,
          urzędnika — który decyduje, co dalej.
        </p>

        <h2 data-testid="wiedza-h2-ai-must-not" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Gdzie AI nie powinna decydować
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Decyzje o&nbsp;statusie prawnym osoby nie należą do&nbsp;modelu
          językowego. AI nie powinna decydować o:
        </p>
        <ul style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}>
          <li>statusie prawnym osoby;</li>
          <li>przyznaniu pobytu czasowego lub stałego;</li>
          <li>nadaniu lub odmowie ochrony międzynarodowej;</li>
          <li>powrocie do kraju pochodzenia;</li>
          <li>uprawnieniu do świadczeń lub zatrudnienia;</li>
          <li>zatwierdzeniu lub odrzuceniu wniosku.</li>
        </ul>
        <div
          data-testid="wiedza-safety-card"
          style={{
            padding: '18px 20px', marginTop: 12, marginBottom: 20,
            background: 'rgba(248,113,113,0.06)',
            border: '1px solid rgba(248,113,113,0.30)',
            borderLeft: '3px solid #F87171',
            borderRadius: 12,
          }}
        >
          <p style={{ fontSize: 12, letterSpacing: 1.2, fontWeight: 700, color: '#F87171', margin: 0, textTransform: 'uppercase' }}>
            Granica systemu
          </p>
          <p
            data-testid="wiedza-safety-body"
            style={{ fontSize: 14, lineHeight: 1.6, marginTop: 8, color: 'rgba(226,232,240,0.92)' }}
          >
            HussarAi nie przyznaje, nie odmawia, nie potwierdza ani nie ustala
            statusu imigracyjnego. Porządkuje akta i&nbsp;historię weryfikacji,
            aby uprawnieni ludzie i&nbsp;organizacje mogli ocenić sprawę.
          </p>
        </div>

        <h2 data-testid="wiedza-h2-integrity" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Integralność akt imigracyjnych pod kontrolą człowieka
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          HussarAi nazywa swoją kategorię wprost: <strong>integralność akt
          imigracyjnych pod kontrolą człowieka</strong>. Słowo „integralność”
          jest tutaj kluczowe. Nie chodzi o&nbsp;to, żeby AI oceniała, czy
          ktoś ma prawo do&nbsp;pobytu. Chodzi o&nbsp;to, żeby akta były:
        </p>
        <ul style={{ paddingLeft: 22, color: 'rgba(226,232,240,0.85)', lineHeight: 1.7, marginBottom: 18 }}>
          <li>kompletne — bez ukrytych braków;</li>
          <li>spójne — daty się zgadzają, dokumenty się nie wykluczają;</li>
          <li>weryfikowalne — historia zmian jest widoczna i podpisana;</li>
          <li>czytelne — uprawniony człowiek może je przeczytać w jednym przebiegu.</li>
        </ul>
        <p
          data-testid="wiedza-doctrine"
          style={{
            fontSize: 15, lineHeight: 1.6, marginBottom: 18,
            color: 'rgba(125,211,252,0.92)', fontStyle: 'italic',
            borderLeft: '3px solid #7DD3FC', paddingLeft: 14,
          }}
        >
          AI przygotowuje. Ludzie decydują. Każde działanie zostawia ślad audytowy.
        </p>

        <h2 data-testid="wiedza-h2-ai-act" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Dlaczego ma to znaczenie w&nbsp;epoce europejskiego nadzoru nad AI
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Europa konsekwentnie buduje warstwę nadzoru nad sztuczną
          inteligencją. EU AI Act i kolejne akty wykonawcze stawiają na
          przejrzystość, ślad audytowy, kontrolę człowieka nad decyzjami
          o&nbsp;wysokim ryzyku oraz odpowiedzialność operatora. Akta
          imigracyjne dotykają wszystkich tych obszarów: dotyczą człowieka,
          mają konsekwencje życiowe i&nbsp;trafiają do&nbsp;administracji
          publicznej.
        </p>
        <p
          data-testid="wiedza-no-certification-claim"
          style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}
        >
          HussarAi jest budowany na epokę europejskiego nadzoru nad AI, ale
          nie twierdzi, że posiada oficjalną certyfikację, zatwierdzenie
          prawne ani status rządowy. Jesteśmy narzędziem porządkującym akta —
          decyzje pozostają po stronie uprawnionych ludzi i&nbsp;organizacji.
        </p>

        <h2 data-testid="wiedza-h2-checklist" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Praktyczna lista — od&nbsp;czego zacząć
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Najprostszy sposób, żeby zbudować integralność własnych akt
          imigracyjnych, to&nbsp;zacząć od&nbsp;jednego miejsca. Statyczna
          lista, przygotowana na <Link
            to="/moje-akta-imigracyjne"
            data-testid="wiedza-link-moje-akta-inline"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            stronie Moje Akta Imigracyjne
          </Link>, dzieli dokumenty na sześć grup: tożsamość i&nbsp;rodzina,
          kraje i&nbsp;podróże, dokumenty, terminy, praca i&nbsp;szkoła, oraz
          pytania do&nbsp;weryfikacji przez człowieka. Można ją wydrukować
          i&nbsp;zabrać do&nbsp;prawnika, NGO, pracodawcy lub urzędu.
        </p>
        <div
          data-testid="wiedza-checklist-cta-row"
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 12,
            marginTop: 8, marginBottom: 24,
          }}
        >
          <Link
            to="/moje-akta-imigracyjne"
            data-testid="wiedza-cta-moje-akta"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(125,211,252,0.12)',
              border: '1px solid rgba(125,211,252,0.40)',
              color: '#7DD3FC', textDecoration: 'none', fontSize: 14,
            }}
          >
            <ListChecks className="w-4 h-4" aria-hidden="true" />
            Zacznij od&nbsp;swoich akt
          </Link>
          <Link
            to="/dashboard-akt-imigracyjnych"
            data-testid="wiedza-cta-case-hub"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 8, fontWeight: 600,
              background: 'rgba(15,23,42,0.55)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14,
            }}
          >
            <FileText className="w-4 h-4" aria-hidden="true" />
            Hub Akt Imigracyjnych
          </Link>
        </div>

        <h2 data-testid="wiedza-h2-helpers" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Dla pomocników i instytucji
        </h2>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Prawnicy, organizacje pomocowe, doradcy relokacyjni, szkoły,
          kościoły, pracodawcy i zespoły HR mogą weryfikować ustrukturyzowane
          akta osoby, zaznaczać braki dokumentów, prosić o&nbsp;tłumaczenia
          i&nbsp;przygotowywać brief do&nbsp;weryfikacji przez człowieka.
          Weryfikują akta, dokumenty i&nbsp;historię — nigdy statusu
          prawnego.
        </p>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 14 }}>
          Instytucje (administracja publiczna, kancelarie, zespoły zgodności)
          mogą przeglądać wersjonowane akta, przypisywać osoby weryfikujące,
          śledzić status procesowy (draft → przygotowane do&nbsp;weryfikacji →
          w&nbsp;weryfikacji → zweryfikowane przez człowieka i&nbsp;podpisane)
          i&nbsp;zachowywać ślad audytowy HMAC-SHA256. Więcej o&nbsp;tej
          warstwie: <Link
            to="/operator-zgodnosci"
            data-testid="wiedza-link-compliance"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            Operator Zgodności dla integralności akt imigracyjnych
          </Link> oraz <Link
            to="/bezpieczenstwo-i-zgodnosc"
            data-testid="wiedza-link-security"
            style={{ color: '#7DD3FC', fontWeight: 600 }}
          >
            Bezpieczeństwo i&nbsp;nadzór nad aktami imigracyjnymi
          </Link>.
        </p>

        <h2 data-testid="wiedza-h2-conclusion" style={{ fontSize: 24, fontWeight: 700, marginTop: 28, marginBottom: 12, color: '#F1F5F9' }}>
          Podsumowanie
        </h2>
        <p
          data-testid="wiedza-conclusion"
          style={{ fontSize: 16, lineHeight: 1.7, color: 'rgba(226,232,240,0.85)', marginBottom: 18 }}
        >
          AI w&nbsp;aktach imigracyjnych ma sens wtedy, kiedy porządkuje
          historię, nie kiedy ją rozstrzyga. Człowiek prowadzi swoją sprawę.
          Pomocnik weryfikuje akta. Instytucja zachowuje odpowiedzialność.
          AI przygotowuje — ludzie decydują, a&nbsp;każde działanie zostawia
          ślad audytowy. Bo&nbsp;Twoja historia nie powinna zniknąć
          w&nbsp;papierach.
        </p>

        <Link
          to="/moje-akta-imigracyjne"
          data-testid="wiedza-final-cta"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 22px', borderRadius: 8, fontWeight: 700,
            background: '#7DD3FC', color: '#0B1220',
            textDecoration: 'none', fontSize: 15, marginTop: 6,
          }}
        >
          <ShieldCheck className="w-4 h-4" aria-hidden="true" />
          Zacznij od&nbsp;swoich akt
        </Link>

        <p
          data-testid="wiedza-doctrine-footer"
          style={{
            marginTop: 36, fontSize: 12, lineHeight: 1.6,
            color: 'rgba(226,232,240,0.55)', fontStyle: 'italic',
          }}
        >
          <Clock className="w-3 h-3 inline" aria-hidden="true" /> AI
          przygotowuje. Ludzie decydują. Każde działanie zostawia ślad audytowy.
        </p>
      </div>
    </main>
  );
};

export default WiedzaAiWAktachImigracyjnych;
